(() => {
  'use strict';

  const hubSelector = '[data-lds-accessories-hub]';
  const customDesignModalSelector = '[data-lds-custom-design-modal]';
  const colorValue = radio => {
    const label = radio?.dataset.label?.trim() || '';
    if (label) return label === 'Flow' ? 'Flow RGB' : label;
    const value = radio?.value?.trim() || '';
    return value.toLowerCase() === 'flow' ? 'Flow RGB' : value;
  };

  const clamp = value => Math.max(0, Math.min(100, value));

  const initComparison = comparison => {
    if (!comparison || comparison.dataset.comparisonReady === 'true') return;

    const stage = comparison.querySelector('.v2-comparison__stage');
    const input = comparison.querySelector('.v2-comparison__input');
    if (!stage || !input || stage.querySelectorAll('.v2-comparison__layer img').length < 2) return;

    comparison.dataset.comparisonReady = 'true';
    comparison.classList.add('is-enhanced');

    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let dragging = false;
    let pendingTouch = false;

    const update = (requestedValue = Number(input.value)) => {
      const numericValue = Number(requestedValue);
      const value = clamp(Number.isFinite(numericValue) ? numericValue : 50);
      const rounded = Math.round(value);
      input.value = String(value);
      input.setAttribute('aria-valuenow', String(rounded));
      input.setAttribute('aria-valuetext', `${comparison.dataset.beforeLabel || 'Before'} ${rounded}%, ${comparison.dataset.afterLabel || 'After'} ${100 - rounded}%`);
      stage.style.setProperty('--v2-comparison-position', `${value}%`);
    };

    const updateFromClientX = clientX => {
      const bounds = stage.getBoundingClientRect();
      if (bounds.width) update(((clientX - bounds.left) / bounds.width) * 100);
    };

    const endDrag = event => {
      if (pointerId === null || (event && event.pointerId !== pointerId)) return;
      if (stage.releasePointerCapture && stage.hasPointerCapture?.(pointerId)) {
        try { stage.releasePointerCapture(pointerId); } catch {}
      }
      pointerId = null;
      pendingTouch = false;
      dragging = false;
      delete comparison.dataset.comparisonDragging;
    };

    const beginDrag = event => {
      dragging = true;
      pendingTouch = false;
      comparison.dataset.comparisonDragging = 'true';
      if (stage.setPointerCapture) {
        try { stage.setPointerCapture(event.pointerId); } catch {}
      }
    };

    stage.addEventListener('pointerdown', event => {
      if (pointerId !== null || (event.pointerType === 'mouse' && event.button !== 0)) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      pendingTouch = event.pointerType === 'touch';
      if (!pendingTouch) {
        beginDrag(event);
        updateFromClientX(event.clientX);
        event.preventDefault();
      }
    }, {passive: false});

    stage.addEventListener('pointermove', event => {
      if (pointerId === null || event.pointerId !== pointerId) return;
      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;
      if (pendingTouch) {
        if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < 6) return;
        if (Math.abs(deltaY) > Math.abs(deltaX)) {
          endDrag(event);
          return;
        }
        beginDrag(event);
      }
      if (dragging) {
        updateFromClientX(event.clientX);
        event.preventDefault();
      }
    }, {passive: false});

    stage.addEventListener('pointerup', event => {
      if (pointerId !== event.pointerId) return;
      if (!dragging || Math.abs(event.clientX - startX) < 6) updateFromClientX(event.clientX);
      endDrag(event);
    }, {passive: false});
    stage.addEventListener('pointercancel', endDrag);
    input.addEventListener('input', () => update(input.value));
    update(input.value);
  };

  const initComparisons = root => {
    const comparisons = root.matches?.('[data-comparison]') ? [root] : [...(root.querySelectorAll?.('[data-comparison]') || [])];
    comparisons.forEach(initComparison);
  };

  const prefillSillForm = root => {
    const modal = document.querySelector(customDesignModalSelector);
    const form = modal?.querySelector('form');
    if (!modal || modal.hidden || !form) return false;

    const brand = root.dataset.ldsHubBrand?.trim() || 'Dodge';
    const vehicle = form.querySelector('[name="contact[Vehicle brand and model]"]');
    if (vehicle && vehicle.value.trim() === '') vehicle.value = brand;

    const selectedColor = root.dataset.ldsHubSillColor || colorValue(root.querySelector('[data-studio-color]:checked'));
    const color = form.querySelector('[name="contact[LED color]"]');
    if (selectedColor && color) {
      const option = [...color.options].find(item => item.value.toLowerCase() === selectedColor.toLowerCase() || item.textContent.trim().toLowerCase() === selectedColor.toLowerCase());
      if (option) color.value = option.value;
    }

    return true;
  };

  const scheduleSillPrefill = root => {
    const modal = document.querySelector(customDesignModalSelector);
    if (!modal) return;

    let attempts = 0;
    const apply = () => {
      attempts += 1;
      if (prefillSillForm(root) || attempts >= 10) return true;
      window.setTimeout(apply, 50);
      return false;
    };

    if (apply()) return;
    const observer = new MutationObserver(() => {
      if (prefillSillForm(root)) observer.disconnect();
    });
    observer.observe(modal, {childList: true, subtree: true});
    window.setTimeout(() => observer.disconnect(), 3000);
  };

  const init = root => {
    if (!root || root.dataset.ldsAccessoriesHubReady === 'true') return;
    root.dataset.ldsAccessoriesHubReady = 'true';
    initComparisons(root);
    // Keep the chat launcher, without a floating label covering mobile cards.
    const fitChat = () => {
      if (root.dataset.hubChatFitted || !window.tidioChatApi?.adjustStyles) return;
      window.tidioChatApi.adjustStyles('@media(max-width:760px){.widgetLabel{display:none!important}}');
      root.dataset.hubChatFitted = 'true';
    };
    if (window.tidioChatApi) {
      window.tidioChatApi.on('ready', fitChat);
      fitChat();
    } else document.addEventListener('tidioChat-ready', fitChat, {once:true});

    root.querySelectorAll('[data-lds-hub-scroll]').forEach(link => {
      link.addEventListener('click', event => {
        const hash = link.getAttribute('href') || '';
        if (!hash.startsWith('#')) return;
        const target = root.querySelector(hash);
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start',
        });
        if (window.history?.replaceState) window.history.replaceState(null, '', hash);
      });
    });

    root.addEventListener('change', event => {
      if (event.target.matches('[data-studio-color]')) root.dataset.ldsHubSillColor = colorValue(event.target);
    });
    root.addEventListener('click', event => {
      if (event.target.matches('[data-studio-color]')) root.dataset.ldsHubSillColor = colorValue(event.target);
      const trigger = event.target.closest('[data-lds-sill-design-trigger], .acura-info-personal .acura-info-cta');
      if (!trigger || !root.contains(trigger)) return;

      const modal = document.querySelector(customDesignModalSelector);
      if (!modal) return;
      if (!trigger.dataset.ldsCustomDesignTrigger) trigger.dataset.ldsCustomDesignTrigger = 'true';
      if (!trigger.dataset.ldsCustomDesignSource) trigger.dataset.ldsCustomDesignSource = 'dodge_accessories_hub_sill';
      event.preventDefault();
      queueMicrotask(() => scheduleSillPrefill(root));
    });
  };

  const initAll = () => document.querySelectorAll(hubSelector).forEach(init);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll, {once: true});
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
